/**
 * @fileoverview React component that displays the diagnostics
 *
 * @author Varun Vijayaraghavan (vvijayaraghavan@yext.com)
 */
goog.module('yext.m4.confcode.adminconsole.DiagnosticsComponent');

const DiagnosticData = goog.require('yext.m4.errors.DiagnosticData');

// If there's no file name in the diagnostic, default to "Unclassified".
const UNCLASSIFIED = 'Unclassified';

class DiagnosticsComponent extends React.Component {
  constructor(props) {
    super(props);

    this._routeToDiagnostic = this._routeToDiagnostic.bind(this);

    /** @type {!Props} */
    this.props;

    /** @type {!State} */
    this.state = {
      selectedDiagnostic: null,
    };
  }

  /**
   * Groups and sorts the diagnostics by file name and diagnostic type.
   * @return {!Object}
   * @private
   */
  _getGroupedDiagnostics() {
    const group = {};
    for (let error of this.props.diagnosticInfo.errors) {
      const fileName = error.fileLocation ? error.fileLocation.filePath : UNCLASSIFIED;
      if (!group[fileName]) {
        group[fileName] = {
          'errors': [],
          'warnings': [],
        };
      }
      group[fileName]['errors'].push(error);
    }
    for (let warning of this.props.diagnosticInfo.warnings) {
      const fileName = warning.fileLocation ? warning.fileLocation.filePath : UNCLASSIFIED;
      if (!group[fileName]) {
        group[fileName] = {
          'errors': [],
          'warnings': [],
        };
      }
      group[fileName]['warnings'].push(warning);
    }
    return group;
  }

  /**
   * Gets the diagnostic represented as a string.
   *
   * @param {!DiagnosticData.Diagnostic} diagnostic
   * @private
   */
  _diagnosticAsString(diagnostic) {
    let text = '';
    if (diagnostic.fileLocation) {
      text += `(Row: ${diagnostic.fileLocation.coordinate.row} Col: ${diagnostic.fileLocation.coordinate.column}) `;
    }
    text += diagnostic.message;
    return text;
  }

  /**
   * Gets the selected diagnostic, and calls the `onSelectDiagnostic` call back.
   * @private
   */
  _routeToDiagnostic() {
    if (this.state.selectedDiagnostic) {
      this.props.diagnosticInfo.onSelectDiagnostic(this.state.selectedDiagnostic);
    }
  }

  _renderDiagnostics() {
    let errorIndex = 0;
    let warningIndex = 0;
    const diagnosticsGrouped = this._getGroupedDiagnostics();
    const sortedFileNames = Object.keys(diagnosticsGrouped).sort((f1, f2) => {
      // Unclassified should be on top.
      if (f1 === UNCLASSIFIED) {
        return -1;
      } else if (f2 === UNCLASSIFIED) {
        return 1;
      } else {
        return f1 >= f2 ? -1 : 1;
      }
    });
    const listElement = [];
    for (const fileName of sortedFileNames) {
      diagnosticsGrouped[fileName]['warnings'].forEach(warning => {
        listElement.push(this._getRenderedDiagnosticElement(warning, 'warning', warningIndex));
        warningIndex += 1;
      });
      diagnosticsGrouped[fileName]['errors'].forEach(error => {
        listElement.push(this._getRenderedDiagnosticElement(error, 'error', errorIndex));
        errorIndex += 1;
      });
    }
    return listElement;
  }

  /**
   * @private
   * @param {!DiagnosticData.Diagnostic}  diagnostic
   * @param {!string}                     diagnosticType
   * @param {!number}                     index
   */
  _getRenderedDiagnosticElement(diagnostic, diagnosticType, index) {
    const diagnosticString = this._diagnosticAsString(diagnostic);
    const isSelected = diagnostic.id === (this.state.selectedDiagnostic && this.state.selectedDiagnostic.id);
    const defaultClassName = 'list-group-item list-group-item-action flex-column align-items-start';
    const className = isSelected ? defaultClassName + ' active' : defaultClassName;
    const onClickHandler = (() => {
      this.setState({selectedDiagnostic: diagnostic});
    });
    const onDoubleClickHandler = (() => {
      this._routeToDiagnostic();
    });
    const onEnterKey = (event => {
      // Enter key's keycode
      if (event['keyCode'] === 13) {
        this.setState({selectedDiagnostic: diagnostic});
        this._routeToDiagnostic();
      }
    });

    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/no-static-element-interactions
      <a
        className={className}
        onClick={onClickHandler}
        onDoubleClick={onDoubleClickHandler}
        onKeyUp={onEnterKey}
      >
        <p className="d-flex justify-content-between meta">
          <span className="diagnostic-file-name">{diagnostic.fileLocation ? diagnostic.fileLocation.filePath : 'Unclassified'}</span>
          <span className={`diagnostic-${diagnosticType}-indicator`}>{diagnosticType.toUpperCase()}</span>
        </p>
        <p className="message">
          {diagnosticString}
        </p>
      </a>
    );
  }

  render() {
    return (
      <div className={this.props.active ? 'tab-pane active' : 'tab-pane'} id="tools-diagnostics"
        role="tabpanel">
        <div className="diagnostics-panel flex-grow-1 js-diagnostics-panel list-group">
          {this._renderDiagnostics()}
        </div>
      </div>
    );
  }
}

/** @record */
class DiagnosticInfo {
  constructor() {
    /**
     * @type {!Array<!DiagnosticData.Diagnostic>}
     */
    this.warnings;

    /**
     * @type {!Array<!DiagnosticData.Diagnostic>}
     */
    this.errors;

    /**
     * @type {!function(!DiagnosticData.Diagnostic)}
     */
    this.onSelectDiagnostic;
  }
}

/** @record */
class State {
  constructor() {
    /**
     * @type {?DiagnosticData.Diagnostic}
     */
    this.selectedDiagnostic;
  }
}


/** @record */
class Props {
  constructor() {
    /**
     * @type {!DiagnosticInfo}
     */
    this.diagnosticInfo;
    /**
     * @type boolean
     */
    this.active;
  }
}

exports = DiagnosticsComponent;
